const apiBaseUrl = process.env.REACT_APP_API_URL_BASE

class APIError extends Error {
    constructor(message, status, response) {
        super(message)
        this.name = 'APIError'
        this.status = status
        this.response = response
    }
}

// Generic API call function
export const apiCall = async ({
    endpoint,
    method = 'POST',
    body = null,
    headers = {},
    orgAccessToken = null,
    responseType = 'json', // 'json', 'text', 'blob', 'raw'
    setLoading = null,
    setError = null,
    setProgress = null,
    customHeaders = {},
    timeout = null,
    retries = 0,
    retryDelay = 1000,
}) => {
    // Set loading state if provided
    if (setLoading) setLoading(true)
    if (setProgress) setProgress(0)

    // Setup headers
    const defaultHeaders = new Headers()

    // Add authorization if provided
    if (orgAccessToken) {
        // Handle both string tokens and object with accessToken property
        const token = typeof orgAccessToken === 'string'
            ? orgAccessToken
            : orgAccessToken.accessToken
        if (token) {
            defaultHeaders.append('Authorization', `Bearer ${token}`)
        }
    }

    // Add content-type for JSON bodies
    if (body && typeof body === 'object' && !(body instanceof FormData)) {
        defaultHeaders.append('Content-Type', 'application/json')
    }

    // Merge with any custom headers
    Object.entries(headers).forEach(([key, value]) => {
        defaultHeaders.append(key, value)
    })

    Object.entries(customHeaders).forEach(([key, value]) => {
        defaultHeaders.append(key, value)
    })

    // Setup request options
    const requestOptions = {
        method,
        headers: defaultHeaders,
        redirect: 'follow',
    }

    // Add body if provided
    if (body) {
        if (body instanceof FormData) {
            requestOptions.body = body
        } else if (typeof body === 'object') {
            requestOptions.body = JSON.stringify(body)
        } else {
            requestOptions.body = body
        }
    }

    // Add timeout
    let timeoutId = null    
    if (timeout) {
        const controller = new AbortController()
        timeoutId = setTimeout(() => controller.abort(), timeout)
        requestOptions.signal = controller.signal
    }

    // Retry logic
    let lastError
    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            const response = await fetch(`${apiBaseUrl}/${endpoint}`, requestOptions)
            if (timeout) {
                clearTimeout(timeoutId)
            }

            // Check if response is ok
            if (!response.ok) {
                throw new APIError(
                    `HTTP error! status: ${response.status}: ${response.statusText}`,
                    response.status,
                    response
                )
            }

            // Handle different response types
            let result
            switch (responseType) {
                case 'json':
                    result = await response.json()
                    break
                case 'text':
                    result = await response.text()
                    break
                case 'blob':
                    result = await response.blob()
                    break
                case 'raw':
                    result = response
                    break
                default:
                    result = await response.json()
            }

            // Success - reset states and return
            if (setLoading) setLoading(false)
            if (setProgress) setProgress(100)
            if (setError) setError(null)

            return result

        } catch (error) {
            lastError = error
            if (timeout) {
                clearTimeout(timeoutId)
            }

            // If it's not the last attempt, wait and retry
            if (attempt < retries) {
                console.log(`Retry attempt ${attempt + 1} after ${retryDelay}ms`)
                await new Promise(resolve => setTimeout(resolve, retryDelay))
                continue
            }

            // Final attempt failed
            console.error(`Error calling ${endpoint}:`, error)
            if (setLoading) setLoading(false)
            if (setProgress) setProgress(0)
            if (setError) setError(error.message || true)
            throw error
        }
    }
}

// Specialized wrapper for common patterns
export const apiGet = (endpoint, options = {}) => 
    apiCall({ ...options, endpoint, method: 'GET' })

export const apiPost = (endpoint, body, options = {}) => 
    apiCall({ ...options, endpoint, method: 'POST', body })

export const apiPut = (endpoint, body, options = {}) => 
    apiCall({ ...options, endpoint, method: 'PUT', body })

export const apiDelete = (endpoint, options = {}) => 
    apiCall({ ...options, endpoint, method: 'DELETE' })

// File upload helper
export const apiUpload = (endpoint, files, additionalData = {}, options = {}) => {
    const formData = new FormData()
    
    // Handle single or multiple files
    if (Array.isArray(files)) {
        files.forEach((file, index) => {
            formData.append(`file_${index}`, file)
        })
    } else {
        formData.append('file', files)
    }

    // Add any additional data
    Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value)
    })

    return apiCall({
        ...options,
        endpoint,
        method: 'POST',
        body: formData,
    })
}

// Batch API calls with progress
export const apiBatch = async (calls, options = {}) => {
    const { setProgress, setError, parallel = true } = options
    const total = calls.length
    let completed = 0

    // Initialize progress for batch operation
    if (setProgress) {
        setProgress(0)
    }

    const updateProgress = () => {
        completed++
        if (setProgress) {
            const progressPercent = Math.round((completed / total) * 100)
            setProgress(progressPercent)
        }
    }

    // Clear any existing error state
    if (setError) {
        setError(null)
    }

    try {
        if (parallel) {
            // Execute all calls in parallel
            const promises = calls.map(call => {
                // Remove progress/error handlers from individual calls to prevent conflicts
                const cleanCall = {
                    ...call,
                    setProgress: null,
                    setError: null,
                    setLoading: null
                }
                
                return apiCall(cleanCall).then(result => {
                    updateProgress()
                    return result
                }).catch(error => {
                    updateProgress() // Still update progress even on error
                    throw error
                })
            })
            return await Promise.all(promises)
        } else {
            // Execute calls sequentially
            const results = []
            for (const call of calls) {
                try {
                    // Remove progress/error handlers from individual calls
                    const cleanCall = {
                        ...call,
                        setProgress: null,
                        setError: null,
                        setLoading: null
                    }
                    
                    const result = await apiCall(cleanCall)
                    updateProgress()
                    results.push(result)
                } catch (error) {
                    updateProgress() // Still update progress even on error
                    throw error
                }
            }
            return results
        }
    } catch (error) {
        // Reset progress on error
        if (setProgress) {
            setProgress(0)
        }
        if (setError) {
            setError(error.message || true)
        }
        throw error
    }
}

// Export for backward compatibility
export default apiCall 